/**
 * Multi-step lead form with enhanced qualification and scoring
 */

import * as React from 'react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle, 
  Calculator,
  Star,
  Euro,
  Clock,
  Building2
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { 
  Lead, 
  ServiceInterest, 
  BudgetBand, 
  Timeline, 
  LeadScoring, 
  CRMIntegration,
  LeadAction 
} from '@/utils/leadScoring';
import { EUAnalytics } from '@/utils/euAnalytics';
import type { SwissLanguage } from '@/utils/languageTypes';

interface EnhancedLeadFormProps {
  prefilledService?: ServiceInterest;
  prefilledCity?: string;
  variant?: 'modal' | 'inline' | 'popup';
  onSuccess?: (lead: Lead) => void;
  crmWebhookUrl?: string;
}

const EnhancedLeadForm: React.FC<EnhancedLeadFormProps> = ({
  prefilledService,
  prefilledCity,
  variant = 'inline',
  onSuccess,
  crmWebhookUrl
}) => {
  const { currentLanguage } = useLanguage();
  const { toast } = useToast();
  
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actions, setActions] = useState<LeadAction[]>([]);
  
  const [formData, setFormData] = useState({
    // Step 1: Service & Budget
    service_interest: prefilledService || 'webdesign' as ServiceInterest,
    budget_band: '7-15k' as BudgetBand,
    timeline: '1-3m' as Timeline,
    
    // Step 2: Company Info
    company: '',
    industry: '',
    current_website: '',
    main_challenge: '',
    
    // Step 3: Contact Info
    name: '',
    email: '',
    phone: '',
    consent_marketing: false
  });

  const translations = {
    de: {
      step1Title: 'Projekt & Budget',
      step2Title: 'Über Ihr Unternehmen',
      step3Title: 'Kontaktdaten',
      service: 'Gewünschte Leistung',
      budget: 'Projektbudget',
      timeline: 'Zeitrahmen',
      company: 'Firmenname',
      industry: 'Branche',
      website: 'Aktuelle Website (optional)',
      challenge: 'Hauptherausforderung',
      name: 'Ihr Name',
      email: 'E-Mail-Adresse',
      phone: 'Telefon (optional)',
      consent: 'Ich möchte Updates und Angebote erhalten',
      nextStep: 'Weiter',
      prevStep: 'Zurück',
      submit: 'Kostenloses Konzept erhalten',
      submitSuccess: 'Vielen Dank! Wir melden uns innerhalb von 2 Stunden.',
      required: 'Pflichtfeld'
    },
    en: {
      step1Title: 'Project & Budget',
      step2Title: 'About Your Business',
      step3Title: 'Contact Details',
      service: 'Service Interest',
      budget: 'Project Budget',
      timeline: 'Timeline',
      company: 'Company Name',
      industry: 'Industry',
      website: 'Current Website (optional)',
      challenge: 'Main Challenge',
      name: 'Your Name',
      email: 'Email Address',
      phone: 'Phone (optional)',
      consent: 'I want to receive updates and offers',
      nextStep: 'Next',
      prevStep: 'Back',
      submit: 'Get Free Proposal',
      submitSuccess: 'Thank you! We\'ll reach out within 2 hours.',
      required: 'Required'
    },
    fr: {
      step1Title: 'Projet & Budget',
      step2Title: 'À Propos de Votre Entreprise',
      step3Title: 'Coordonnées',
      service: 'Service Souhaité',
      budget: 'Budget Projet',
      timeline: 'Délai',
      company: 'Nom de l\'Entreprise',
      industry: 'Secteur',
      website: 'Site Web Actuel (optionnel)',
      challenge: 'Défi Principal',
      name: 'Votre Nom',
      email: 'Adresse Email',
      phone: 'Téléphone (optionnel)',
      consent: 'Je souhaite recevoir des mises à jour et offres',
      nextStep: 'Suivant',
      prevStep: 'Retour',
      submit: 'Obtenir Proposition Gratuite',
      submitSuccess: 'Merci ! Nous vous contactons sous 2 heures.',
      required: 'Obligatoire'
    },
    it: {
      step1Title: 'Progetto & Budget',
      step2Title: 'Sulla Vostra Azienda',
      step3Title: 'Dati di Contatto',
      service: 'Servizio di Interesse',
      budget: 'Budget Progetto',
      timeline: 'Tempistica',
      company: 'Nome Azienda',
      industry: 'Settore',
      website: 'Sito Web Attuale (opzionale)',
      challenge: 'Sfida Principale',
      name: 'Il Vostro Nome',
      email: 'Indirizzo Email',
      phone: 'Telefono (opzionale)',
      consent: 'Voglio ricevere aggiornamenti e offerte',
      nextStep: 'Avanti',
      prevStep: 'Indietro',
      submit: 'Richiedi Proposta Gratuita',
      submitSuccess: 'Grazie! Vi contatteremo entro 2 ore.',
      required: 'Obbligatorio'
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const serviceOptions = {
    de: {
      webdesign: 'Webdesign',
      seo: 'SEO',
      paid_ads: 'Google & Meta Ads',
      ai_video: 'AI-Videos',
      ecommerce: 'E-Commerce',
      uxui: 'UX/UI Design',
      '3d': '3D Modeling',
      arvr: 'AR/VR Experiences',
      ai_influencer: 'AI Influencer',
      automation: 'Business Automation'
    },
    en: {
      webdesign: 'Web Design',
      seo: 'SEO',
      paid_ads: 'Google & Meta Ads',
      ai_video: 'AI Videos',
      ecommerce: 'E-Commerce',
      uxui: 'UX/UI Design',
      '3d': '3D Modeling',
      arvr: 'AR/VR Experiences',
      ai_influencer: 'AI Influencer',
      automation: 'Business Automation'
    },
    fr: {
      webdesign: 'Web Design',
      seo: 'Référencement',
      paid_ads: 'Publicités Google & Meta',
      ai_video: 'Vidéos IA',
      ecommerce: 'E-Commerce',
      uxui: 'Design UX/UI',
      '3d': 'Modélisation 3D',
      arvr: 'Expériences AR/VR',
      ai_influencer: 'Influenceur IA',
      automation: 'Automatisation Business'
    },
    it: {
      webdesign: 'Web Design',
      seo: 'SEO',
      paid_ads: 'Pubblicità Google & Meta',
      ai_video: 'Video AI',
      ecommerce: 'E-Commerce',
      uxui: 'Design UX/UI',
      '3d': 'Modellazione 3D',
      arvr: 'Esperienze AR/VR',
      ai_influencer: 'Influencer AI',
      automation: 'Automazione Business'
    }
  };

  const currentServiceOptions = serviceOptions[currentLanguage as keyof typeof serviceOptions];

  // Track form started
  React.useEffect(() => {
    const action: LeadAction = {
      type: 'lead_form_started',
      timestamp: new Date().toISOString(),
      context: 'enhanced_lead_form',
      page_url: window.location.href
    };
    setActions(prev => [...prev, action]);
  }, []);

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.name || !formData.company) {
      toast({
        title: "Error",
        description: t.required,
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Create lead object
      const lead: Lead = {
        id: `lead_${Date.now()}`,
        email: formData.email,
        name: formData.name,
        company: formData.company,
        phone: formData.phone,
        
        lead_source: 'SEO', // Assume organic for now
        market_region: LeadScoring.getMarketRegion(currentLanguage as SwissLanguage, prefilledCity as any || 'zurich'),
        target_city: prefilledCity as any || 'zurich',
        service_interest: formData.service_interest,
        bundle_interest: LeadScoring.suggestBundle({
          service_interest: formData.service_interest,
          budget_band: formData.budget_band,
          target_city: prefilledCity as any || 'zurich'
        } as Lead),
        language: currentLanguage as SwissLanguage,
        
        budget_band: formData.budget_band,
        timeline: formData.timeline,
        lead_score: LeadScoring.calculateScore(actions, formData),
        
        consent_marketing: formData.consent_marketing,
        consent_timestamp: formData.consent_marketing ? new Date().toISOString() : undefined,
        
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        first_touch_page: window.location.href,
        last_touch_page: window.location.href,
        session_count: 1
      };

      // Send to CRM
      await CRMIntegration.sendToCRM(lead, crmWebhookUrl);

      // Track in GA4
      EUAnalytics.trackEULeadSubmit(
        lead.target_city,
        lead.service_interest,
        lead.language,
        {
          formType: 'enhanced_lead_form',
          budget: lead.budget_band,
          timeline: lead.timeline,
          leadScore: lead.lead_score
        }
      );

      toast({
        title: "Success",
        description: t.submitSuccess
      });

      if (onSuccess) {
        onSuccess(lead);
      }

    } catch (error) {
      console.error('Lead submission error:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="service">{t.service}</Label>
              <Select
                value={formData.service_interest}
                onValueChange={(value) => setFormData({ ...formData, service_interest: value as ServiceInterest })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(currentServiceOptions).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="budget">{t.budget}</Label>
              <Select
                value={formData.budget_band}
                onValueChange={(value) => setFormData({ ...formData, budget_band: value as BudgetBand })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="≤3k">≤ CHF 3,000</SelectItem>
                  <SelectItem value="3-7k">CHF 3,000 - 7,000</SelectItem>
                  <SelectItem value="7-15k">CHF 7,000 - 15,000</SelectItem>
                  <SelectItem value="15-30k">CHF 15,000 - 30,000</SelectItem>
                  <SelectItem value="30k+">CHF 30,000+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="timeline">{t.timeline}</Label>
              <Select
                value={formData.timeline}
                onValueChange={(value) => setFormData({ ...formData, timeline: value as Timeline })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ASAP">ASAP</SelectItem>
                  <SelectItem value="2-4w">2-4 Wochen</SelectItem>
                  <SelectItem value="1-3m">1-3 Monate</SelectItem>
                  <SelectItem value=">3m">&gt;3 Monate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="company">{t.company} *</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Firma AG"
              />
            </div>

            <div>
              <Label htmlFor="industry">{t.industry}</Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                placeholder="E-Commerce, SaaS, Beratung..."
              />
            </div>

            <div>
              <Label htmlFor="website">{t.website}</Label>
              <Input
                id="website"
                value={formData.current_website}
                onChange={(e) => setFormData({ ...formData, current_website: e.target.value })}
                placeholder="https://example.com"
              />
            </div>

            <div>
              <Label htmlFor="challenge">{t.challenge}</Label>
              <Input
                id="challenge"
                value={formData.main_challenge}
                onChange={(e) => setFormData({ ...formData, main_challenge: e.target.value })}
                placeholder="Mehr Leads, bessere Conversion..."
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="name">{t.name} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Max Mustermann"
              />
            </div>

            <div>
              <Label htmlFor="email">{t.email} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="max@firma.ch"
              />
            </div>

            <div>
              <Label htmlFor="phone">{t.phone}</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+41 76 123 45 67"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="consent"
                checked={formData.consent_marketing}
                onCheckedChange={(checked) => setFormData({ ...formData, consent_marketing: !!checked })}
              />
              <Label htmlFor="consent" className="text-sm">
                {t.consent}
              </Label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="w-full max-w-lg mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center">
            <Star className="w-5 h-5 text-primary mr-2" />
            {step === 1 && t.step1Title}
            {step === 2 && t.step2Title}
            {step === 3 && t.step3Title}
          </CardTitle>
          <Badge variant="outline">{step}/3</Badge>
        </div>
        <Progress value={(step / 3) * 100} className="w-full" />
      </CardHeader>

      <CardContent>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {stepContent()}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between mt-8">
          {step > 1 ? (
            <Button variant="outline" onClick={handlePrev}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t.prevStep}
            </Button>
          ) : (
            <div />
          )}

          {step < 3 ? (
            <Button onClick={handleNext}>
              {t.nextStep}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting}
              className="neo-button"
            >
              {isSubmitting ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {t.submit}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EnhancedLeadForm;